import * as React from 'react';
import { Avatar as AvatarPrimitive } from 'radix-ui';
import { initials } from '@/lib/auth';
import { cn } from '@/lib/utils';

/**
 * Avatar (DESIGN_SYSTEM §4): sm 28 / md 40 / lg 56 / xl 88 px; Google photo when available,
 * else initials on primary-soft.
 */
type AvatarSize = 'sm' | 'md' | 'lg' | 'xl';

function Avatar({
  className,
  size = 'sm',
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root> & { size?: AvatarSize }) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      data-size={size}
      className={cn(
        'group/avatar relative flex size-7 shrink-0 overflow-hidden rounded-full select-none data-[size=lg]:size-14 data-[size=md]:size-10 data-[size=xl]:size-22',
        className,
      )}
      {...props}
    />
  );
}

function AvatarImage({ className, ...props }: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn('aspect-square size-full object-cover', className)}
      {...props}
    />
  );
}

function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        'flex size-full items-center justify-center rounded-full bg-primary-soft text-caption font-semibold text-primary group-data-[size=md]/avatar:text-body group-data-[size=lg]/avatar:text-h3 group-data-[size=lg]/avatar:font-sans group-data-[size=xl]/avatar:text-h1',
        className,
      )}
      {...props}
    />
  );
}

/**
 * A person's avatar. Decorative by default: the name is always shown next to it. `tone="gold"`
 * puts the initials on the brand gold, for an avatar on the blue band (profile header).
 */
function PersonAvatar({
  name,
  src,
  size = 'sm',
  tone = 'default',
  className,
}: {
  name: string;
  src?: string | null;
  size?: AvatarSize;
  tone?: 'default' | 'gold';
  className?: string;
}) {
  return (
    <Avatar size={size} aria-hidden="true" {...(className ? { className } : {})}>
      {src ? <AvatarImage src={src} alt="" /> : null}
      <AvatarFallback {...(tone === 'gold' ? { className: 'bg-accent-gold text-on-accent' } : {})}>
        {initials(name)}
      </AvatarFallback>
    </Avatar>
  );
}

export { Avatar, AvatarFallback, AvatarImage, PersonAvatar, type AvatarSize };
